import { ReactComponent as CloseIcon } from 'app/assets/img/close_icon.svg';
import ModalContainer from 'app/components/ModalContainer';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectTranslations } from '../App/slice/selectors';
import moment from 'moment';
import StyledCollapse, {
  CollapsePanelWrapper,
} from 'app/components/StyledCollapse';
import { ReactComponent as CopyIcon } from 'app/assets/img/copy-icon.svg';
import {
  FareFamilyData,
  FareFamilyRuleItem,
  getFareFamilyByFareBasis,
} from 'app/services/fareFamilyApi';
interface IDialogFlightInformationProps {
  flights: any[];
  isShow: boolean;
  onCloseDialog: () => void;
  fareFamilies: FareFamilyData[];
}
interface AirportInfo {
  code?: string;
  name?: string;
  terminal?: string | number;
}
const formatDuration = (duration: string) => {
  const h = Number(duration.slice(0, 2));
  const m = Number(duration.slice(2));

  if (h && m) return `${h}h${m}p`;
  if (h) return `${h}h`;
  return `${m}p`;
};
const getDuration = (departureTime: string, arrivalTime: string): string => {
  const start = new Date(departureTime).getTime();
  const end = new Date(arrivalTime).getTime();
  const diffMinutes = Math.floor((end - start) / 60000);
  const h = Math.floor(diffMinutes / 60);
  const m = diffMinutes % 60;
  return `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}`;
};
const DialogFlightInformation: React.FC<
  IDialogFlightInformationProps
> = props => {
  const { flights, isShow, onCloseDialog, fareFamilies } = props;
  const translations = useSelector(selectTranslations);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<any>(null);
  const formatAirportText = (data?: AirportInfo) => {
    if (!data) return '';
    const parts = [
      data.code,
      data.name,
      data.terminal ? `T${data.terminal}` : '',
    ];
    return parts.filter(Boolean).join(' ');
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(flights?.[0]?.pnr || '');
      setCopied(true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {}
  };
  const getAllFareRules = flight => {
    const fareBasis = flight.fareBasis || '';
    const cabinId = undefined;
    if (fareBasis && fareFamilies.length > 0) {
      const fareFamily = getFareFamilyByFareBasis(
        fareBasis,
        fareFamilies,
        cabinId,
      );
      if (fareFamily && fareFamily.fareFamilyRules?.length > 0) {
        const flightOrigin = flight.departure || '';
        const flightDestination = flight.arrival || '';
        const activeRulesForRoute = fareFamily.fareFamilyRules.filter(
          r =>
            r.status === 1 &&
            r.departureCode === flightOrigin &&
            r.arriveCode === flightDestination,
        );
        const uniqueRules = activeRulesForRoute.reduce((acc, rule) => {
          if (!acc.find(r => r.fareRuleId === rule.fareRuleId)) {
            acc.push(rule);
          }
          return acc;
        }, [] as FareFamilyRuleItem[]);

        return {
          class: fareFamily.name || '',
          rules: uniqueRules,
        };
      }
    }
    return {
      class: '',
      rules: [],
    };
  };
  const formatRuleValue = (rule: FareFamilyRuleItem) => {
    if (rule.type === 2) return translations?.['b2b.txt.no'];
    if (rule.type === 1)
      return rule.value || translations?.['b2b.txt.calculateFee'];
    return rule.value || translations?.['b2b.txt.allow'];
  };
  return (
    <ModalContainer
      isShow={isShow}
      onCloseDialog={onCloseDialog}
      maskClosable={false}
      modalPadding="16px 24px 32px"
      modalHeight="initial"
      styleSlots={{
        iconWrapperStyle: {
          margin: '0 16px 0 0',
        },
        modalContentStyle: {
          maxWidth: 980,
          width: '96vw',
          maxHeight: '96vh',
        },
        modalBodyStyle: {
          height: 'fit-content',
        },
      }}
      modalProps={{ destroyOnClose: true }}
      CloseIcon={<CloseIcon width={12} height={12} />}
    >
      <TitleModel>
        {translations?.['b2b.txt.flightInformation'] || ''}
      </TitleModel>
      <SubTitle>
        PNR
        <span>: {flights?.[0]?.pnr}</span>{' '}
        <CopyButton onClick={handleCopy}>
          {copied ? <TickIcon>✓</TickIcon> : <CopyIconCss />}
        </CopyButton>
      </SubTitle>
      <Line />
      {flights?.map((flight, index) => {
        const allFareRules = getAllFareRules(flight);
        return (
          <FlightLegCard key={`flight-${index}`}>
            <Header>
              <Title>
                {index === 0
                  ? translations?.['b2b.txt.departureLeg']
                  : translations?.['b2b.txt.returnLeg']}
              </Title>
              {/* <Price>{`${formatNumber(flight?.totalAmount || 0, '.')}đ`}</Price> */}
            </Header>
            <Content>
              <Timeline>
                <span className="dot" />
                <span className="line" />
                <span className="dot" />
              </Timeline>
              <Info>
                <Row>
                  <Time>{moment(flight?.departureTime).format('HH:mm')}</Time>
                  <Location>{formatAirportText(flight.departureInfo)}</Location>
                </Row>
                <Meta>
                  <Duration>
                    {formatDuration(
                      getDuration(flight?.departureTime, flight?.arrivalTime),
                    )}
                  </Duration>
                  <Aircraft>
                    {' '}
                    {flight.carrier + flight.flightNumber || ''}
                  </Aircraft>
                </Meta>

                <Row>
                  <Time>{moment(flight?.arrivalTime).format('HH:mm')}</Time>
                  <Location>{formatAirportText(flight.arrivalInfo)}</Location>
                </Row>
              </Info>
            </Content>
            <StyledCollapse defaultActiveKey="">
              <CollapsePanelWrapper
                style={{
                  paddingLeft: 10,
                  paddingRight: 10,
                }}
                key={1}
                header={
                  <div style={{ fontSize: 14 }}>
                    {translations?.['b2b.txt.baggageInformation']}
                  </div>
                }
              >
                <CollapseContent>
                  {allFareRules.rules.length > 0 ? (
                    <div>
                      <Class>{allFareRules.class}</Class>
                      <Baggage>
                        {translations?.['b2b.txt.flight-luggage']} +{' '}
                        {
                          translations?.[
                            'b2b.txt.flight-ticket-price-regulations'
                          ]
                        }
                      </Baggage>
                      {allFareRules.rules.map((rule, idx) => (
                        <ClassContentWrap key={`rule-${rule.id}-${idx}`}>
                          {rule.fareRuleName}:
                          <ClassValue>{formatRuleValue(rule)}</ClassValue>
                        </ClassContentWrap>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <Baggage>
                        {
                          translations?.[
                            'b2b.txt.flight-ticket-price-regulations'
                          ]
                        }
                      </Baggage>
                      <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                        {translations?.['b2b.txt.haveNoData']}
                      </div>
                    </div>
                  )}
                </CollapseContent>
              </CollapsePanelWrapper>
            </StyledCollapse>
          </FlightLegCard>
        );
      })}
    </ModalContainer>
  );
};
export default DialogFlightInformation;
const TitleModel = styled.div`
  font-size: 16px;
  font-weight: 600;
`;
const Line = styled.div`
  height: 1px;
  background-color: #e5e5e5;
  margin: 16px -24px;
`;
const FlightLegCard = styled.div`
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  font-size: 14px;
  border: 1px solid #e5e5e5;
  margin-bottom: 16px;
`;
const Header = styled.div`
  background: #f5f5f5;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Title = styled.div`
  font-weight: 500;
`;
const Price = styled.div`
  font-weight: 600;
`;
const Content = styled.div`
  display: flex;
  padding: 14px;
  padding-bottom: 0;
`;
const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 12px;
  padding-top: 6px;
  padding-bottom: 6px;
  .dot {
    width: 8px;
    height: 8px;
    background: #262626;
    border-radius: 50%;
  }
  .line {
    flex: 1;
    width: 2px;
    background: #ddd;
    margin: 0px 0;
  }
`;
const Info = styled.div`
  flex: 1;
`;
const Row = styled.div`
  display: flex;
  gap: 8px;
`;

const Time = styled.div`
  min-width: 48px;
  font-weight: 600;
`;

const Location = styled.div``;
const Meta = styled.div`
  margin: 4px 0 8px 56px;
  font-size: 12px;
  display: flex;
  gap: 12px;
`;

const Duration = styled.div`
  color: #8d8d8d;
`;

const Aircraft = styled.div`
  color: var(--color-9);
`;
const CollapseContent = styled.div`
  margin-top: -10px;
`;
const Class = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;
const Baggage = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
`;
const ClassContentWrap = styled.div`
  font-size: 12px;
  font-weight: 600;
  gap: 6px;
  display: flex;
  margin-bottom: 4px;
`;
const ClassValue = styled.span`
  font-weight: 400;
  color: #8d8d8d;
`;
const SubTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
`;
const CopyButton = styled.div`
  border: none;
  padding: 0 12px;
  cursor: pointer;
  // width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  // height: 50px;
`;
const CopyIconCss = styled(CopyIcon)`
  height: 15px;
  filter: brightness(0) saturate(100%) invert(64%) sepia(63%) saturate(1246%)
    hue-rotate(345deg) brightness(99%) contrast(101%);
`;
const TickIcon = styled.div`
  height: 10px;
  width: 20px;
  color: var(--color-9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
`;
